
import React, { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import {
  Box,
  Button,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import { DropLi } from "../../../hooks";
import { formatCurrency } from "../../../hooks/Currency";

import ReportPrintButton from "../../reports/ReportPrintButton";

//------------------------------------------------
// Reusable Purchase Return Details
//------------------------------------------------
const PurchaseReturnContent = ({
  viewData,
  store,
  returnReasons,
  t,
  printMode = false,
}) => {
  const items = Array.isArray(viewData?.purchReturnItems)
    ? viewData.purchReturnItems
    : [];

  const purchase = viewData?.purchase || {};
  const supplier = purchase.supplier || {};
  const returnUser = viewData?.user || {};

  //------------------------------------------------
  // Return Reason
  //------------------------------------------------
  const getReasonName = (id) => {
    const reason = returnReasons.find(
      (item) => String(item.id) === String(id)
    );

    return reason?.name || "";
  };

  //------------------------------------------------
  // Totals
  //------------------------------------------------
  const totalQuantity = items.reduce(
    (sum, item) => sum + Number(item.qty || 0),
    0
  );

  const totalAmount = items.reduce(
    (sum, item) =>
      sum +
      Number(item.qty || 0) *
        Number(item.product?.purchPrice || 0),
    0
  );

  const money = (value) =>
    formatCurrency(Number(value) || 0, store);

  const returnUserName = [
    returnUser.firstName,
    returnUser.lastName,
  ]
    .filter(Boolean)
    .join(" ");

  //------------------------------------------------
  // Render
  //------------------------------------------------
  return (
    <Box>
      {/* Report Title */}
      <Box sx={{ textAlign: "center", mt: 2, mb: 3 }}>
        <Typography
          variant="h5"
          sx={{ fontWeight: "bold" }}
        >
          {t("returnItemDetails")}
        </Typography>

        <Box
          sx={{
            width: "80%",
            borderBottom: "1px solid #ccc",
            mx: "auto",
            mt: 2,
          }}
        />
      </Box>

      {/* Returned Products */}
      <TableContainer sx={{ mt: 2 }}>
        <Table size="small" aria-label="purchase return items">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>
                {t("itemName")}
              </TableCell>

              <TableCell sx={{ fontWeight: "bold" }}>
                {t("barcode")}
              </TableCell>

              <TableCell sx={{ fontWeight: "bold" }}>
                {t("returnReason")}
              </TableCell>

              <TableCell
                align="right"
                sx={{ fontWeight: "bold" }}
              >
                {t("quantity")}
              </TableCell>

              <TableCell
                align="right"
                sx={{ fontWeight: "bold" }}
              >
                {t("price")}
              </TableCell>

              <TableCell
                align="right"
                sx={{ fontWeight: "bold" }}
              >
                {t("total")}
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {items.map((item, index) => {
              const quantity = Number(item.qty || 0);
              const price = Number(
                item.product?.purchPrice || 0
              );

              return (
                <TableRow key={item.id ?? index}>
                  <TableCell>
                    {item.product?.name || ""}
                  </TableCell>

                  <TableCell>
                    {item.product?.barcode || ""}
                  </TableCell>

                  <TableCell>
                    {getReasonName(item.returnReason)}
                  </TableCell>

                  <TableCell align="right">
                    {quantity}
                  </TableCell>

                  <TableCell align="right">
                    {money(price)}
                  </TableCell>

                  <TableCell align="right">
                    {money(quantity * price)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Divider sx={{ my: 2 }} />

      {/* Return Summary */}
      <Grid container spacing={2}>
        {/* Left Summary */}
        <Grid item xs={12} sm={6}>
          <TableContainer>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell>
                    {t("referenceNo")}:
                  </TableCell>

                  <TableCell align="right">
                    {purchase.id ?? ""}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell>
                    {t("supplier")}:
                  </TableCell>

                  <TableCell align="right">
                    {supplier.company || ""}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell>
                    {t("returnUser")}:
                  </TableCell>

                  <TableCell align="right">
                    {returnUserName}
                  </TableCell>
                </TableRow>

                {/* Receipt Link: screen only */}
                {!printMode && purchase.receipt && (
                  <TableRow>
                    <TableCell>
                      {t("purchaseReceipt")}:
                    </TableCell>

                    <TableCell align="right">
                      <a
                        href={purchase.receipt}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {t("viewReceipt")}
                      </a>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        {/* Right Summary */}
        <Grid item xs={12} sm={6}>
          <TableContainer>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell>
                    {t("returnDate")}:
                  </TableCell>

                  <TableCell align="right">
                    {viewData?.returnDate || ""}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell>
                    {t("totalItem")}:
                  </TableCell>

                  <TableCell align="right">
                    {totalQuantity}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell
                    sx={{ fontWeight: "bold" }}
                  >
                    {t("total")}:
                  </TableCell>

                  <TableCell
                    align="right"
                    sx={{ fontWeight: "bold" }}
                  >
                    {money(totalAmount)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    </Box>
  );
};

//------------------------------------------------
// View Return Items
//------------------------------------------------
const ViewReturnItems = (props) => {
  const { t } = useTranslation();

  const { setShowForm, viewData } = props;

  const _store = useSelector(
    (state) => state.pos.defStore
  );

  const { ReturnReason } = DropLi();

  const reportRef = useRef(null);

  const returnReasons = useMemo(
    () => ReturnReason || [],
    [ReturnReason]
  );

  //------------------------------------------------
  // Render
  //------------------------------------------------
  return (
    <Grid container className="p-20">
      {/* Print Preview Button */}
      <Grid
        item
        xs={12}
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          mb: 1,
        }}
      >
        <ReportPrintButton
          reportRef={reportRef}
          paperSize="Letter"
          reportTitle={t("returnItemDetails")}
        />
      </Grid>

      {/* Screen Purchase Return Details */}
      <Grid item xs={12}>
        <PurchaseReturnContent
          viewData={viewData}
          store={_store}
          returnReasons={returnReasons}
          t={t}
        />
      </Grid>

      {/* Print-only Purchase Return Details */}
      <Grid item xs={12}>
        <Box
          ref={reportRef}
          className="purchase-return-details-print"
          sx={{
            position: "absolute",
            left: "-10000px",
            top: 0,
            width: "1000px",
            backgroundColor: "#fff",
            color: "#000",
            p: 2,
          }}
        >
          <PurchaseReturnContent
            viewData={viewData}
            store={_store}
            returnReasons={returnReasons}
            t={t}
            printMode={true}
          />
        </Box>
      </Grid>

      {/* Close Button */}
      <Grid
        item
        xs={12}
        className="tx-center mt-40"
      >
        <Button
          onClick={() => setShowForm(false)}
          className="btn-error"
          size="large"
          sx={{ minWidth: "150px" }}
        >
          {t("close")}
        </Button>
      </Grid>
    </Grid>
  );
};

export default ViewReturnItems;
